import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AuthLayout from './AuthLayout';
import GoogleButton from './GoogleButton';

export default function SignupForm() {
  const navigate = useNavigate();
  const { signUp, signInWithGoogle, error: authError } = useAuth();

  const [step, setStep] = useState(1); // 1: auth, 2: family
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Auth fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');

  // Family fields
  const [familyName, setFamilyName] = useState('');
  const [memberNames, setMemberNames] = useState(['']);

  const addMember = () => {
    if (memberNames.length < 10) {
      setMemberNames([...memberNames, '']);
    }
  };

  const removeMember = (index) => {
    if (memberNames.length > 1) {
      setMemberNames(memberNames.filter((_, i) => i !== index));
    }
  };

  const updateMember = (index, value) => {
    const updated = [...memberNames];
    updated[index] = value;
    setMemberNames(updated);
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);
    try {
      await signInWithGoogle();
      // OAuth will redirect to /create-family
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleContinue = (e) => {
    e.preventDefault();
    setError(null);

    if (!email || !password || !fullName) {
      setError('Please fill in all fields');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    // Pre-fill the user's name as first member
    if (memberNames[0] === '') {
      setMemberNames([fullName]);
    }

    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!familyName.trim()) {
      setError('Please enter a family name');
      return;
    }

    const validMembers = memberNames.filter(n => n.trim());
    if (validMembers.length === 0) {
      setError('Please add at least one family member');
      return;
    }

    setLoading(true);

    try {
      await signUp({
        email,
        password,
        fullName,
        familyName,
        memberNames: validMembers
      });
      navigate('/success');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Auth credentials
  if (step === 1) {
    return (
      <AuthLayout title="Create your account" subtitle="Start collecting family memories today">
        <form onSubmit={handleContinue} className="space-y-6">
          {(error || authError) && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error || authError}
            </div>
          )}

          <div>
            <label htmlFor="fullName" className="label">Your name</label>
            <input
              id="fullName"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="input"
              placeholder="Jane Smith"
              required
            />
          </div>

          <div>
            <label htmlFor="email" className="label">Email address</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              placeholder="jane@example.com"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="label">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              placeholder="At least 6 characters"
              required
              minLength={6}
            />
          </div>

          <button type="submit" className="btn-primary w-full py-3">
            Continue
          </button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">or</span>
            </div>
          </div>

          <GoogleButton onClick={handleGoogleSignIn} loading={loading} />

          <p className="text-center text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/signin" className="text-primary-600 hover:text-primary-500 font-medium">
              Sign in
            </Link>
          </p>
        </form>
      </AuthLayout>
    );
  }

  // Step 2: Family setup
  return (
    <AuthLayout title="Create your family" subtitle="Add your family members who will contribute memories">
      <form onSubmit={handleSubmit} className="space-y-6">
        {(error || authError) && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error || authError}
          </div>
        )}

        <div>
          <label htmlFor="familyName" className="label">Family name</label>
          <input
            id="familyName"
            type="text"
            value={familyName}
            onChange={(e) => setFamilyName(e.target.value)}
            className="input"
            placeholder="The Smiths"
            required
          />
          <p className="mt-1 text-xs text-gray-500">
            This will be the name of your family newsletter
          </p>
        </div>

        <div>
          <label className="label">Family members</label>
          <p className="text-xs text-gray-500 mb-3">
            Add everyone who will submit photos and stories
          </p>

          <div className="space-y-3">
            {memberNames.map((name, index) => (
              <div key={index} className="flex gap-2">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => updateMember(index, e.target.value)}
                  className="input flex-1"
                  placeholder={index === 0 ? 'Your name' : `Family member ${index + 1}`}
                />
                {memberNames.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeMember(index)}
                    className="px-3 py-2 text-gray-400 hover:text-red-500 transition-colors"
                    title="Remove member"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>

          {memberNames.length < 10 && (
            <button
              type="button"
              onClick={addMember}
              className="mt-3 text-sm text-primary-600 hover:text-primary-500 font-medium flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add another member
            </button>
          )}
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setStep(1)}
            className="btn-secondary flex-1 py-3"
          >
            Back
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary flex-1 py-3"
          >
            {loading ? 'Creating...' : 'Create Family'}
          </button>
        </div>
      </form>
    </AuthLayout>
  );
}
